import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { Subscription, Subject, interval, takeUntil } from 'rxjs';
import { DataService, ServicecallService } from 'src/app';
import {
  DataBroadcast,
  DataBroadcastModel,
  PlanogramEntity,
  ZonePlanModel,
} from 'src/app/model';
import { FilterFields } from 'src/app/model/shared/components/filter-panel';
import { PlanogramSharedService } from '../../services';

interface HistoryItem {
  TIS: string;
  trackedState: string;
}
@Component({
  selector: 'app-zone-planogram',
  templateUrl: './zone-planogram.component.html',
  styleUrl: './zone-planogram.component.scss',
})
export class ZonePlanogramComponent implements OnInit, OnDestroy {
  @Input()
  set zone(zonePlan: ZonePlanModel) {
    this._zone = zonePlan;
    this._initialize();
  }

  @Input()
  set filters(value: { [key: string]: { [key: string]: boolean } }) {
    this.planogramFilters = { ...value };
    this.planogramSharedService.updateFilters(this.planogramFilters);
  }

  @Input()
  set isOpen(value: boolean) {
    if (value) this._initialize(false);
  }

  get zone() {
    return this._zone;
  }

  @Output() headerDetials = new EventEmitter<any>();
  @Output() filterDetails = new EventEmitter<any>();

  public filterFields: FilterFields;
  public planogramFilters: { [key: string]: { [key: string]: boolean } } = {};
  public planogramSummaryData: any[];
  public planogramEntityList: any[];
  public isHistoryDrawerOpen = false;
  public historyTitle = 'History';
  public historyData: HistoryItem[] = [];
  public selectedEntity: any = null;

  // Color mappings from shared service
  get summaryColorMapping() {
    return this.planogramSharedService.summaryColorMapping;
  }

  get backgroundColorMapping() {
    return this.planogramSharedService.backgroundColorMapping;
  }

  get historyBackgroundColorMapping() {
    return this.planogramSharedService.historyBackgroundColorMapping;
  }

  // Properties
  private _zone: ZonePlanModel;

  // Subscription
  private _dataServiceEventSubscription: Subscription;
  private _planogramSummarySubscription: Subscription;
  private _planogramEntityListSubscription: Subscription;
  private _planogramEntityHistorySubscription: Subscription;
  private _filterFieldsSubscription: Subscription;
  private _periodicApiSubscription: Subscription;
  private _destroy$ = new Subject<void>();

  constructor(
    public _service: ServicecallService,
    private _dataService: DataService,
    private planogramSharedService: PlanogramSharedService
  ) {}

  ngOnInit(): void {
    // Subscribe to shared service observables
    this._planogramSummarySubscription =
      this.planogramSharedService.planogramSummary$.subscribe((data) => {
        this.planogramSummaryData = data;
        this.headerDetials.emit({
          content: this.planogramSummaryData,
          colorMapping: this.summaryColorMapping,
        });
      });

    this._planogramEntityListSubscription =
      this.planogramSharedService.planogramEntityList$.subscribe((data) => {
        this.planogramEntityList = data;
      });

    this._filterFieldsSubscription =
      this.planogramSharedService.filterFields$.subscribe((data) => {
        this.filterFields = data;
        this.filterDetails.emit(this.filterFields);
      });

    // API Refresh
    this._dataServiceEventSubscription = this._dataService.events.subscribe({
      next: (broadcast: DataBroadcastModel) => {
        if (
          broadcast.name === DataBroadcast?.SITE_CHANGED ||
          broadcast?.name === DataBroadcast.API_REFRESH ||
          broadcast?.name === DataBroadcast.TRACKER_ENTITY_LOCATION_CHANGED ||
          broadcast?.name === DataBroadcast.PAGE_CONTROL_DATE_CHANGE
        ) {
          this._initialize();
        }
      },
    });

    // Start periodic API calls
    this._startPeriodicApiCalls();
  }

  ngOnDestroy(): void {
    // Trigger the destroy subject
    this._destroy$.next();
    this._destroy$.complete();

    // Unsubscribe from all subscriptions
    this._dataServiceEventSubscription?.unsubscribe();
    this._planogramSummarySubscription?.unsubscribe();
    this._planogramEntityListSubscription?.unsubscribe();
    this._planogramEntityHistorySubscription?.unsubscribe();
    this._filterFieldsSubscription?.unsubscribe();
    this._periodicApiSubscription?.unsubscribe();

    this.headerDetials.emit({
      content: null,
      colorMapping: null,
    });
  }

  /**
   * Initialize API call
   */
  private _initialize(isLoaderNeeded: boolean = true) {
    if (this._zone) {
      this.planogramSharedService.initialize(
        this._zone,
        isLoaderNeeded,
        this.planogramFilters
      );
    }
  }

  /**
   * Starts periodic API calls every 60 seconds
   */
  private _startPeriodicApiCalls(): void {
    // Create an interval observable that emits every 60 seconds
    this._periodicApiSubscription = interval(
      this._service.getConfiguration('tenantGenericConfig')?.[
        `2DPlanogramTimer`
      ] ?? 60000
    )
      .pipe(
        // Use takeUntil to automatically unsubscribe when the component is destroyed
        takeUntil(this._destroy$)
      )
      .subscribe(() => {
        this._initialize(false);
      });
  }

  /**
   * track by for performance improvement
   * @param {numer} index - index
   * @param {PlanogramEntity} entity - entity details
   * @return Unique identifier for tracking changes for tile
   */
  public trackByEntityId(index: number, entity: PlanogramEntity): string {
    return entity?._id;
  }

  /**
   * track by for timelapse segments
   * @param {number} index - index
   * @param {any} segment - timelapse segment
   * @return The index as string
   */
  public trackBySegmentIndex(index: number, segment: any): string {
    return index.toString();
  }

  /**
   * Gets the status name for the given tracked state
   * @param trackedState - The tracked state value
   * @returns The name of the status from planogramSummaryData
   */
  public getStatusName(trackedState: string | null): string {
    return this.planogramSharedService.getStatusName(trackedState);
  }

  /**
   * Calculate the width percentage for a timelapse segment
   * @param value - Segment value
   * @param total - Total value of all segments
   * @returns Percentage width as string with % suffix
   */
  public calculateSegmentWidth(value: string, total: number): string {
    return this.planogramSharedService.calculateSegmentWidth(value, total);
  }

  /**
   * Opens the history drawer for the specified entity
   * @param entity - The entity to show history for
   */
  public openHistoryDrawer(entity: PlanogramEntity): void {
    this.selectedEntity = entity;
    this.historyTitle = `History - ${entity?.entityName}`;
    this.isHistoryDrawerOpen = true;

    // Fetch history data
    this._fetchEntityHistory(entity.entityId);

    // Prevent the click from bubbling to the tile
    event?.stopPropagation();
  }

  /**
   * Closes the history drawer
   */
  public closeHistoryDrawer(): void {
    this.isHistoryDrawerOpen = false;
    this.historyData = [];
  }

  /**
   * Fetches history data for an entity
   * @param entityId - The ID of the entity
   */
  private _fetchEntityHistory(entityId: string): void {
    this._planogramEntityHistorySubscription = this.planogramSharedService
      .getEntityHistory(entityId)
      .subscribe((data) => {
        this.historyData = data;
      });
  }
}
